<template>
  <div class="bg auth-screen">
    <div class="container pt-80">
      <div class="card">
        <div class="card-body">
          <h2 class="card-title">Create a New Password</h2>
          <form>
            <div class="form-group">
              <label for="password">Password</label>
              <input type="password" class="form-control form-control-lg" @input="validPassword(user.password), validateConfirmPassword()" v-model="user.password" placeholder="Enter password" required id="password" />
            </div>
            <div class="form-group">
              <label for="confirm-password">Confirm Password</label>
              <input type="password" class="form-control form-control-lg" @input="validateConfirmPassword()" v-model="user.confirmPassword" placeholder="Re-enter password" required id="confirm-password" />
              <p class="text-danger" v-if="passwordNotMatched">
                Passwords do not match. Please try again.
              </p>
              <div class="regex-req">
                <div class="req-title">Password requirements:</div>
                <ul class="validation-list">
                  <li>
                    <span
                      v-if="passwordReq.showErrors"
                      :class="{
                        success: passwordReq.min8char,
                        error: !passwordReq.min8char
                      }"
                      aria-hidden="true"
                    ></span>
                    <span v-else class="initial-state" aria-hidden="true"></span>
                    min. 8 characters
                  </li>
                  <li>
                    <span
                      v-if="passwordReq.showErrors"
                      :class="{
                        success: passwordReq.anyLetter,
                        error: !passwordReq.anyLetter
                      }"
                      aria-hidden="true"
                    ></span>
                    <span v-else class="initial-state" aria-hidden="true"></span>
                    at least 1 letter
                  </li>
                  <li>
                    <span
                      v-if="passwordReq.showErrors"
                      :class="{
                        success: passwordReq.number,
                        error: !passwordReq.number
                      }"
                      aria-hidden="true"
                    ></span>
                    <span v-else class="initial-state" aria-hidden="true"></span>
                    at least 1 number (12345)
                  </li>
                  <li>
                    <span
                      v-if="passwordReq.showErrors"
                      :class="{
                        success: passwordReq.specialChar,
                        error: !passwordReq.specialChar
                      }"
                      aria-hidden="true"
                    ></span>
                    <span v-else class="initial-state" aria-hidden="true"></span>
                    at least 1 symbol (!@#$%&)
                  </li>
                  <li>
                    <span
                      v-if="passwordReq.showErrors"
                      :class="{
                        success: passwordReq.passwordReusedCheck,
                        error: !passwordReq.passwordReusedCheck
                      }"
                      aria-hidden="true"
                    ></span>
                    <span v-else class="initial-state" aria-hidden="true"></span>
                    cannot be reused within a cycle of 6 passwords
                  </li>
                </ul>
              </div>
              <p class="text-danger mt-16" v-if="errorMsg">{{ errorMsg }}</p>
            </div>
            <div class="form-group mb-0">
              <button type="button" class="btn btn-primary btn-block" :disabled="!enableSubmitBtn" v-on:click.prevent="changePassword">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";

export default {
  name: "CreatePassword",
  data() {
    return {
      passwordNotMatched: false,
      user: {
        password: "",
        confirmPassword: ""
      },
      passwordReq: {
        min8char: false,
        anyLetter: false,
        number: false,
        specialChar: false,
        passwordReusedCheck: false,
        showErrors: false,
        passwordMatched: false
      },
      errorMsg: null,
      apiCallInProgress: false
    };
  },
  computed: {
    ...mapGetters(["token"]),
    enableSubmitBtn() {
      if (this.passwordReq.min8char && this.passwordReq.anyLetter && this.passwordReq.number && this.passwordReq.specialChar && this.passwordReq.passwordReusedCheck && this.passwordReq.passwordMatched && !this.apiCallInProgress) {
        return true;
      } else {
        return false;
      }
    }
  },
  mounted() {
    this.mobileNo = this.$route.params.mobile;
    if (this.token) {
      this.userLogout();
    }
  },
  methods: {
    ...mapActions(["userLogout"]),
    changePassword() {
      this.apiCallInProgress = true;
      this.$api
        .setNewPasswordFact({
          token: this.$route.params.token,
          newPassword: this.user.password
        })
        .then(
          response => {
            this.correctOTP = true;
            this.successMsg = response.body.message;
            this.$router.push({
              name: "login"
            });
          },
          error => {
            this.errorMsg = error.body && error.body.message ? error.body.message : "Error while changing the password";
          }
        );
    },
    validateConfirmPassword() {
      this.passwordNotMatched = false;
      this.passwordReq.passwordMatched = false;
      if (
        this.user.password === this.user.confirmPassword &&
        this.passwordReq.min8char &&
        /* this.passwordReq.lowercase &&
                this.passwordReq.uppercase && */
        this.passwordReq.anyLetter &&
        this.passwordReq.number &&
        this.passwordReq.specialChar
      ) {
        this.passwordNotMatched = false;
        this.passwordReq.passwordMatched = true;
        this.validatePasswordReUseCheck();
      } else if (this.user.confirmPassword === "") {
        this.passwordNotMatched = false;
      } else if (this.user.password === this.user.confirmPassword) {
        this.passwordNotMatched = false;
        this.passwordReq.passwordMatched = true;
      } else {
        this.passwordNotMatched = true;
      }
    },
    validPassword(password) {
      this.passwordReq.showErrors = true;
      const numberPattern = new RegExp("^(?=.*[0-9]).+$");
      const specialPattern = new RegExp("^(?=.*[!\"@#$%&'(\)*+,-./:;<=>?\[\\\]^_{|}~]).+$");
      const letterPattern = new RegExp("^(?=.*[a-zA-Z]).+$");

      // empty password check
      if (password === "") {
        this.passwordReq.showErrors = false;
      } else if (password.length >= 8) {
        this.passwordReq.min8char = true;
      } else if (password.length < 8) {
        this.passwordReq.min8char = false;
      }

      // any letter pattern check
      if (letterPattern.test(password)) {
        this.passwordReq.anyLetter = true;
      } else {
        this.passwordReq.anyLetter = false;
      }

      //Atleast 1 number
      if (numberPattern.test(password)) {
        this.passwordReq.number = true;
      } else {
        this.passwordReq.number = false;
      }

      //Atleast 1 special char
      if (specialPattern.test(password)) {
        this.passwordReq.specialChar = true;
      } else {
        this.passwordReq.specialChar = false;
      }
      this.validatePasswordReUseCheck();
    },
    validatePasswordReUseCheck: _.debounce(function() {
      this.apiCallInProgress = true;
      this.$api
        .setNewCheckPasswordFact({
          token: this.$route.params.token,
          newPassword: this.user.password
        })
        .then(
          () => {
            this.apiCallInProgress = false;
            this.passwordReq.passwordReusedCheck = true;
          },
          () => {
            this.apiCallInProgress = false;
            this.passwordReq.passwordReusedCheck = false;
          }
        );
    }, 1000)
  }
};
</script>
<style scoped lang="scss" src="@/assets/auth-screen.scss"></style>
